/**
 * nipplejs floating thumbstick mounted as a DOM overlay over the lower
 * 60% of the Phaser canvas. Drag → cardinal direction → intentRouter
 * under the 'joystick' source. Step 3 of the touch-controls plan.
 *
 * Lifecycle:
 *   - mountJoystick(scene)               creates the zone div + nipple
 *                                        + attaches Phaser SLEEP/WAKE
 *                                        listeners
 *   - on Phaser.Scenes.Events.SLEEP      tears down (Battle/Draft/Menu/
 *                                        EndRunScene each .sleep
 *                                        WorldScene)
 *   - on Phaser.Scenes.Events.WAKE       recreates
 *   - handle.destroy()                   final teardown (e.g., user
 *                                        switches to Classic mode)
 *
 * Defensive: nipplejs.create() is wrapped in try/catch per design-doc
 * §"Defensive paths 2B(a)". On failure we log, leave the zone div
 * removed, and return a no-op handle. Keyboard cursors + classic D-pad
 * stay alive for the session — Step 4 will surface a one-line in-world
 * dialogue suggesting refresh + classic-mode fallback.
 *
 * DOM accessibility: zone is role="presentation" + aria-hidden="true".
 * Screen-reader users will use Classic D-pad mode (or keyboard cursors
 * on desktop). Per design-doc §"Accessibility considerations".
 */
import nipplejs from 'nipplejs';
import Phaser from 'phaser';
import { intentRouter } from './intentRouter';
import { Z_JOYSTICK } from '../ui/zLayers';

const ZONE_ID = 'poke-joystick-zone';
/** --specular-edge family for both ring + thumb per DESIGN.md "Joystick"
 *  component vocab — white-glass surfaces that read as material rather
 *  than a colored affordance. Pre-Future-Cartridge these were warm
 *  --ink-soft rgba (111, 106, 86, X); now neutral specular white.
 *  Both passed as rgba so the alpha lands on the nipplejs-rendered
 *  element directly. The back div has no internal opacity (renders
 *  our alpha verbatim); the front div hardcodes opacity: 0.5 on top,
 *  so the front rgba alpha gets halved at render. Targets per spec:
 *  back ~12% effective (--specular-edge), front ~20% effective
 *  (--specular-edge-emphasis after the 0.5 multiply). Combined with
 *  restOpacity: 0 (forced when mode==='dynamic') + 100 ms fadeTime,
 *  the joystick stays quiet between presses. */
const COLOR_BACK = 'rgba(255, 255, 255, 0.12)'; // --specular-edge
const COLOR_FRONT = 'rgba(255, 255, 255, 0.4)'; // → 0.20 effective via lib ×0.5

export interface JoystickHandle {
  destroy(): void;
  /** Set whether the zone catches pointer events. WorldScene calls
   *  setEnabled(false) when the party-sheet overlay opens (or, in
   *  step 5, when an orb drag is in progress) so the joystick can't
   *  steal pointers from those gestures. Pass true to restore.
   *  Idempotent. */
  setEnabled(enabled: boolean): void;
}

/** Click signature: short hold + minimal drift. Matches the gates the
 *  scene-level handleTap uses, so a tap forwarded through here is
 *  recognized identically to one that landed directly on the canvas. */
const TAP_MAX_DURATION_MS = 250;
const TAP_MAX_DRIFT_PX = 8;

/** Scene event name emitted from the joystick zone when a capture-phase
 *  click is detected. WorldScene subscribes to it and feeds handleTap.
 *  Exported so consumers don't have to repeat the magic string. */
export const TAP_PASSTHROUGH_EVENT = 'poke:tap-passthrough';

/** Payload shape — screen-space client coords of the original pointer. */
export interface TapPassthroughPayload {
  clientX: number;
  clientY: number;
}

/** Wire capture-phase tap-passthrough on the joystick zone. Pointers
 *  that look like a click emit a scene event (TAP_PASSTHROUGH_EVENT)
 *  carrying their screen coords; WorldScene converts to a synthetic
 *  pointer and runs the same handleTap as a native canvas tap. Drags
 *  pass through to nipplejs's own bubble-phase handlers — we never
 *  call preventDefault, so both code paths see the pointer. */
function attachTapPassthrough(zone: HTMLDivElement, scene: Phaser.Scene): void {
  const starts = new Map<number, { x: number; y: number; t: number }>();

  zone.addEventListener(
    'pointerdown',
    (e: PointerEvent) => {
      starts.set(e.pointerId, { x: e.clientX, y: e.clientY, t: e.timeStamp });
    },
    { capture: true },
  );

  const onEnd = (e: PointerEvent): void => {
    const start = starts.get(e.pointerId);
    starts.delete(e.pointerId);
    if (!start) return;
    const duration = e.timeStamp - start.t;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const distance = Math.hypot(dx, dy);
    if (duration > TAP_MAX_DURATION_MS || distance > TAP_MAX_DRIFT_PX) return;
    scene.events.emit(TAP_PASSTHROUGH_EVENT, {
      clientX: start.x,
      clientY: start.y,
    } satisfies TapPassthroughPayload);
  };

  zone.addEventListener('pointerup', onEnd, { capture: true });
  zone.addEventListener('pointercancel', onEnd, { capture: true });
}

/**
 * Mount the joystick layer onto the Phaser canvas's parent container.
 * Returns a handle whose .destroy() removes the listener subscriptions
 * + the DOM container. WorldScene calls this once on create() when
 * controlMode === 'direct'; setControlMode toggles mount/unmount at
 * runtime.
 */
export function mountJoystick(scene: Phaser.Scene): JoystickHandle {
  let collection: ReturnType<typeof nipplejs.create> | null = null;
  let zone: HTMLDivElement | null = null;

  function create(): void {
    if (collection || zone) return;

    // Append into #game (the Phaser parent div) so the overlay shares
    // the canvas's stacking context. position: fixed on #game already
    // creates a positioning root, so absolute layout below resolves
    // relative to it.
    const parent = document.getElementById('game') ?? document.body;
    const div = document.createElement('div');
    div.id = ZONE_ID;
    div.setAttribute('role', 'presentation');
    div.setAttribute('aria-hidden', 'true');
    div.style.position = 'absolute';
    div.style.left = '0';
    div.style.right = '0';
    // Cover the full canvas height EXCEPT the orb-strip band. The orb
    // band = STRIP_BOTTOM_INSET (16) + ORB_LEAD_SIZE (60) + buffer (8) =
    // 84 px; keeping that excluded prevents the joystick from stealing
    // pointer events from the orb chrome. The Pokéball-glyph at top-
    // right (Z_OVERLAY_GLYPHS=26) layers above this zone (Z_JOYSTICK=20)
    // and intercepts its own taps via DOM z-order, so leaving the top
    // half open lets the user anchor the joystick anywhere on the
    // canvas. Capture-phase tap-passthrough still routes short taps in
    // this zone to WorldScene.handleTap, so tap-on-NPC keeps working.
    div.style.top = '0';
    div.style.bottom = '84px';
    // Layer above the canvas; Z_JOYSTICK sits below the orb-chrome
    // surfaces so the orbs paint on top of the joystick zone even
    // when the strip's container bounds overlap.
    div.style.zIndex = String(Z_JOYSTICK);
    // iOS Safari: kill scroll-bounce, double-tap zoom, text selection.
    div.style.touchAction = 'none';
    div.style.userSelect = 'none';
    div.style.webkitUserSelect = 'none';
    div.style.setProperty('-webkit-touch-callout', 'none');
    div.style.pointerEvents = 'auto';
    parent.appendChild(div);
    zone = div;

    try {
      collection = nipplejs.create({
        zone: div,
        mode: 'dynamic',
        color: { front: COLOR_FRONT, back: COLOR_BACK },
        size: 96,
        threshold: 0.25,
        fadeTime: 100,
        restJoystick: false,
        multitouch: false,
      });
    } catch (err) {
      console.error('[joystick] nipplejs.create failed:', err);
      div.remove();
      zone = null;
      collection = null;
      return;
    }

    // 'move' fires continuously while the user drags. data.direction is
    // null below the 0.25 threshold — that's the dead zone; clear all
    // four directions so the player stops. When above it, we pick the
    // cardinal from |vector.x| vs |vector.y| (dominant axis) rather
    // than reading data.direction.angle, which returns 8-way values
    // ('up-right' etc.) that would leave the player motionless in the
    // diagonal octants. Since the game has no diagonal movement, every
    // above-threshold position resolves to a cardinal here and crossing
    // the 45° line snaps direction immediately — no swing-through-dead-
    // zone delay. nipplejs flips screen y so vector.y is positive-up.
    //
    // Handler signature in nipplejs v1: a single InternalEvent<JoystickEventData>
    // argument; the JoystickEventData lives on .data.
    collection.on('move', (evt) => {
      const data = evt.data;
      if (!data.direction) {
        intentRouter.setIntent('up', false, 'joystick');
        intentRouter.setIntent('down', false, 'joystick');
        intentRouter.setIntent('left', false, 'joystick');
        intentRouter.setIntent('right', false, 'joystick');
        return;
      }
      const { x, y } = data.vector;
      const horizontal = Math.abs(x) >= Math.abs(y);
      intentRouter.setIntent('right', horizontal && x > 0, 'joystick');
      intentRouter.setIntent('left', horizontal && x < 0, 'joystick');
      intentRouter.setIntent('up', !horizontal && y > 0, 'joystick');
      intentRouter.setIntent('down', !horizontal && y < 0, 'joystick');
    });

    // 'end' fires on touchend / touchcancel / pointerup. Drop all four
    // directions so the in-flight chain-press flow naturally stops at
    // the next tween boundary.
    collection.on('end', () => {
      intentRouter.clearSource('joystick');
    });

    // Tap-passthrough (Step 5 — design doc §"DOM/canvas integration").
    // The zone div has pointer-events: auto so nipplejs can capture
    // drags. That same setting blocks short taps from reaching the
    // canvas behind it, so Phaser never sees tap-on-NPC / tap-on-sign
    // gestures that land in the lower 60%. Capture-phase listeners on
    // the zone record pointerdown timing + position; on pointerup, if
    // the gesture is a "click" (< 250 ms AND < 8 px movement — matches
    // WorldScene.handleTap's gates), we emit a scene event with the
    // tap's screen coords. WorldScene subscribes and feeds handleTap
    // a synthetic pointer — same code path as a tap that landed
    // directly on the canvas, just bypassing Phaser's DOM listener
    // (which the zone div sits above and shadows).
    attachTapPassthrough(div, scene);
  }

  function teardown(): void {
    if (collection) {
      collection.destroy();
      collection = null;
    }
    if (zone) {
      zone.remove();
      zone = null;
    }
    intentRouter.clearSource('joystick');
  }

  // Initial mount
  create();

  // SLEEP/WAKE — Battle/Draft/Menu/EndRunScene all sleep WorldScene
  // when launched. The zone div would otherwise eat taps on the
  // overlay scene's interactive elements. Tear down on SLEEP, recreate
  // on WAKE.
  const onSleep = (): void => teardown();
  const onWake = (): void => create();
  scene.events.on(Phaser.Scenes.Events.SLEEP, onSleep);
  scene.events.on(Phaser.Scenes.Events.WAKE, onWake);

  return {
    destroy(): void {
      scene.events.off(Phaser.Scenes.Events.SLEEP, onSleep);
      scene.events.off(Phaser.Scenes.Events.WAKE, onWake);
      teardown();
    },
    setEnabled(enabled: boolean): void {
      if (!zone) return;
      // pointer-events: auto = nipplejs sees touches; none = they fall
      // through to whatever's underneath (the canvas). Don't display:
      // none — the zone needs to stay in layout so the joystick
      // re-anchors at the same spot when re-enabled.
      zone.style.pointerEvents = enabled ? 'auto' : 'none';
      // Clear any in-flight direction intent on disable; otherwise an
      // up-direction in-flight when the sheet opens would persist as
      // walking-into-the-modal once the sheet closes.
      if (!enabled) intentRouter.clearSource('joystick');
    },
  };
}
